import React, { useState } from 'react';
import './AuthModal.css';
import { signup, login } from '../services/authService';
import { supabase } from '../services/supabaseClient';

export default function AuthModal({ isOpen, onClose }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (mode === 'forgot') {
        console.log('[Forgot Password] Sending reset for:', email);
        const resetResult = await supabase.auth.api.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/reset-password' });
        console.log('[Forgot Password] Response:', JSON.stringify(resetResult));
        if (resetResult.error) throw new Error(resetResult.error.message);
        setSuccess('Password reset link sent to your email.');
        setMode('login');
      } else if (mode === 'signup') {
        const data = await signup(email, password, firstName, lastName);
        if (data.session) {
          setSuccess('Account created!');
          onClose();
        } else {
          setSuccess('Check your email to confirm your account, then log in.');
          setMode('login');
        }
      } else {
        await login(email, password);
        setSuccess('Login successful!');
        onClose();
      }
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal">
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>{mode === 'login' ? 'Login' : mode === 'signup' ? 'Sign Up' : 'Reset Password'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
              />
            </>
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          {mode !== 'forgot' && (
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          )}
          <button type="submit" disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Login' : mode === 'signup' ? 'Sign Up' : 'Send Reset Link'}
          </button>
        </form>
        {mode === 'login' && (
          <div className="forgot-link">
            <button onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}>Forgot password?</button>
          </div>
        )}
        <div className="auth-divider">
          <span>or</span>
        </div>
        <button
          className="social-btn apple-btn"
          onClick={() =>
            supabase.auth.signIn(
              { provider: 'apple' },
              { redirectTo: window.location.origin },
            )
          }
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
          Sign in with Apple
        </button>
        <button
          className="social-btn google-btn"
          onClick={() =>
            supabase.auth.signIn(
              { provider: 'google' },
              { redirectTo: window.location.origin },
            )
          }
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Sign in with Google
        </button>
        <div className="switch-mode">
          {mode === 'login' && (
            <span>Don't have an account? <button onClick={() => setMode('signup')}>Sign Up</button></span>
          )}
          {mode === 'signup' && (
            <span>Already have an account? <button onClick={() => setMode('login')}>Login</button></span>
          )}
          {mode === 'forgot' && (
            <span>Remember your password? <button onClick={() => setMode('login')}>Login</button></span>
          )}
        </div>
      </div>
    </div>
  );
}
